// src/features/employees/employeeThunks.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// =============================================================================
// BASIC EMPLOYEE OPERATIONS
// =============================================================================

/**
 * Fetch list of employees (Admin sees all, non-admin sees only self).
 * GET /api/employees/
 */
export const fetchEmployeesThunk = createAsyncThunk(
  'employees/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/employees/');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch employees');
    }
  }
);

/**
 * Fetch a single employee by ID
 * GET /api/employees/:id/
 */
export const fetchEmployeeThunk = createAsyncThunk(
  'employees/fetchOne',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/employees/${employeeId}/`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch employee');
    }
  }
);

/**
 * Create a new employee (only Admin can do)
 * POST /api/employees/
 */
export const createEmployeeThunk = createAsyncThunk(
  'employees/create',
  async (employeeData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/employees/', employeeData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create employee');
    }
  }
);

/**
 * Update an employee
 * PATCH /api/employees/:id/
 */
export const updateEmployeeThunk = createAsyncThunk(
  'employees/update',
  async ({ employeeId, updates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/employees/${employeeId}/`, updates);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update employee');
    }
  }
);

/**
 * Fetch an employee with their targets
 * GET /api/employees/:id/ and GET /api/employee-targets/?employee=:id
 */
export const fetchEmployeeWithTargetsThunk = createAsyncThunk(
  'employees/fetchWithTargets',
  async (employeeId, { rejectWithValue }) => {
    try {
      const employeeResponse = await axiosInstance.get(`/api/employees/${employeeId}/`);
      const employee = employeeResponse.data;
      
      const targetsParams = new URLSearchParams({ 
        employee: employeeId 
      });
      const targetsResponse = await axiosInstance.get(`/api/employee-targets/?${targetsParams}`);
      
      return {
        ...employee,
        targets: targetsResponse.data.results || []
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch employee with targets');
    }
  }
);

/**
 * Bulk create employees (admin only)
 * POST /api/employees/bulk_create/
 */
export const bulkCreateEmployeesThunk = createAsyncThunk(
  'employees/bulkCreate',
  async (employeesData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/employees/bulk_create/', {
        employees: employeesData
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to bulk create employees');
    }
  }
);

/**
 * Reset employee password (admin only)
 * POST /api/employees/:id/reset_password/
 */
export const resetEmployeePasswordThunk = createAsyncThunk(
  'employees/resetPassword',
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/api/employees/${employeeId}/reset_password/`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to reset employee password');
    }
  }
);

// =============================================================================
// BASIC INCENTIVE OPERATIONS (Original)
// =============================================================================

/**
 * Fetch basic incentive summary for an employee (original)
 * GET /api/employees/:id/incentive_summary/
 */
export const fetchEmployeeIncentiveSummaryThunk = createAsyncThunk(
  'employees/fetchIncentiveSummary',
  async ({ employeeId, year, month }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      
      const response = await axiosInstance.get(
        `/api/employees/${employeeId}/incentive_summary/?${params}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch incentive summary');
    }
  }
);

// =============================================================================
// ENHANCED INCENTIVE OPERATIONS
// =============================================================================

/**
 * Fetch enhanced incentive summary with monthly breakdown
 * GET /api/employees/:id/incentive_summary/ (enhanced version)
 */
export const fetchEnhancedEmployeeIncentiveSummaryThunk = createAsyncThunk(
  'employees/fetchEnhancedIncentiveSummary',
  async ({ employeeId, year, month }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      
      const response = await axiosInstance.get(
        `/api/employees/${employeeId}/incentive_summary/?${params}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch enhanced incentive summary');
    }
  }
);

/**
 * Fetch detailed incentive history for an employee with pagination
 * GET /api/employees/:id/detailed_incentive_history/
 */
export const fetchDetailedIncentiveHistoryThunk = createAsyncThunk(
  'employees/fetchDetailedIncentiveHistory',
  async ({ 
    employeeId, 
    year, 
    month, 
    store_id, 
    status, 
    limit = 20, 
    offset = 0 
  }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit, offset });
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      if (store_id) params.append('store_id', store_id);
      if (status) params.append('status', status);
      
      const response = await axiosInstance.get(
        `/api/employees/${employeeId}/detailed_incentive_history/?${params}`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch detailed incentive history');
    }
  }
);

// =============================================================================
// INCENTIVE TRANSACTIONS
// =============================================================================

/**
 * Fetch incentive transactions 
 * GET /api/incentive-transactions/
 */
export const fetchIncentiveTransactionsThunk = createAsyncThunk(
  'employees/fetchIncentiveTransactions',
  async ({ 
    offset = 0, 
    limit = 20, 
    year, 
    month, 
    status, 
    employee, 
    store 
  } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit, offset });
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      if (status) params.append('status', status);
      if (employee) params.append('employee', employee);
      if (store) params.append('store', store);
      
      const response = await axiosInstance.get(`/api/incentive-transactions/?${params}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch incentive transactions');
    }
  }
);

/**
 * Get incentive transactions summary
 * GET /api/incentive-transactions/summary/
 */
export const fetchIncentiveTransactionsSummaryThunk = createAsyncThunk(
  'employees/fetchIncentiveTransactionsSummary',
  async ({ year, month } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      
      const response = await axiosInstance.get(`/api/incentive-transactions/summary/?${params}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch incentive transactions summary');
    }
  }
);

/**
 * Bulk update transaction status (admin only)
 * POST /api/incentive-transactions/bulk_update_status/
 */
export const bulkUpdateTransactionStatusThunk = createAsyncThunk(
  'employees/bulkUpdateTransactionStatus',
  async ({ transaction_ids, status }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/incentive-transactions/bulk_update_status/', {
        transaction_ids,
        status
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to bulk update transaction status');
    }
  }
);

// =============================================================================
// MONTHLY INCENTIVES
// =============================================================================

/**
 * Fetch monthly incentives
 * GET /api/monthly-incentives/
 */
export const fetchMonthlyIncentivesThunk = createAsyncThunk(
  'employees/fetchMonthlyIncentives',
  async ({ 
    offset = 0, 
    limit = 20, 
    year, 
    month, 
    status, 
    employee 
  } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ limit, offset });
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      if (status) params.append('status', status);
      if (employee) params.append('employee', employee);
      
      const response = await axiosInstance.get(`/api/monthly-incentives/?${params}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch monthly incentives');
    }
  }
);

/**
 * Update monthly incentive status (admin only)
 * PATCH /api/monthly-incentives/:id/
 */
export const updateMonthlyIncentiveStatusThunk = createAsyncThunk(
  'employees/updateMonthlyIncentiveStatus',
  async ({ incentiveId, status, notes }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/monthly-incentives/${incentiveId}/`, {
        status,
        notes
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update monthly incentive status');
    }
  }
);

/**
 * Get pending monthly incentives summary (admin only)
 * GET /api/monthly-incentives/pending_summary/
 */
export const fetchPendingMonthlyIncentivesSummaryThunk = createAsyncThunk(
  'employees/fetchPendingMonthlyIncentivesSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/monthly-incentives/pending_summary/');
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch pending monthly incentives summary');
    }
  }
);

/**
 * Calculate monthly incentives for a specific month (admin only)
 * POST /api/monthly-incentives/calculate_monthly/
 */
export const calculateMonthlyIncentivesThunk = createAsyncThunk(
  'employees/calculateMonthlyIncentives',
  async ({ year, month }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/monthly-incentives/calculate_monthly/', {
        year,
        month
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to calculate monthly incentives');
    }
  }
);

// =============================================================================
// ADMIN OPERATIONS
// =============================================================================

/**
 * Fetch all employees' incentives summary (Admin only)
 * GET /api/employees/all-incentives-summary/
 */
export const fetchAllEmployeesIncentivesSummaryThunk = createAsyncThunk(
  'employees/fetchAllEmployeesIncentivesSummary',
  async ({ year, month, store_id } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      if (store_id) params.append('store_id', store_id);
      
      const response = await axiosInstance.get(`/api/employees/all-incentives-summary/?${params}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch all employees incentives summary');
    }
  }
);

/**
 * Fetch monthly incentives dashboard data (Admin only)
 * GET /api/employees/monthly-incentives-dashboard/
 */
export const fetchMonthlyIncentivesDashboardThunk = createAsyncThunk(
  'employees/fetchMonthlyIncentivesDashboard',
  async ({ year } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      
      const response = await axiosInstance.get(`/api/employees/monthly-incentives-dashboard/?${params}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch monthly incentives dashboard');
    }
  }
);

// =============================================================================
// INDIVIDUAL TRANSACTION OPERATIONS
// =============================================================================

/**
 * Create an individual incentive transaction (admin only)
 * POST /api/incentive-transactions/
 */
export const createIncentiveTransactionThunk = createAsyncThunk(
  'employees/createIncentiveTransaction',
  async (transactionData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/incentive-transactions/', transactionData);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to create incentive transaction');
    }
  }
);

/**
 * Update an individual incentive transaction (admin only)
 * PATCH /api/incentive-transactions/:id/
 */
export const updateIncentiveTransactionThunk = createAsyncThunk(
  'employees/updateIncentiveTransaction',
  async ({ transactionId, updates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/incentive-transactions/${transactionId}/`, updates);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to update incentive transaction');
    }
  }
);

/**
 * Delete an incentive transaction (admin only)
 * DELETE /api/incentive-transactions/:id/
 */
export const deleteIncentiveTransactionThunk = createAsyncThunk(
  'employees/deleteIncentiveTransaction',
  async (transactionId, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/incentive-transactions/${transactionId}/`);
      return transactionId;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to delete incentive transaction');
    }
  }
);

// =============================================================================
// REPORTING AND ANALYTICS
// =============================================================================

/**
 * Get incentive analytics data
 * GET /api/incentive-transactions/analytics/
 */
export const fetchIncentiveAnalyticsThunk = createAsyncThunk(
  'employees/fetchIncentiveAnalytics',
  async ({ year, month, store_id, employee_id } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      if (store_id) params.append('store_id', store_id);
      if (employee_id) params.append('employee_id', employee_id);
      
      const response = await axiosInstance.get(`/api/incentive-transactions/analytics/?${params}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch incentive analytics');
    }
  }
);

/**
 * Export incentive data
 * GET /api/incentive-transactions/export/
 */
export const exportIncentiveDataThunk = createAsyncThunk(
  'employees/exportIncentiveData',
  async ({ year, month, format = 'csv', store_id, employee_id } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      if (format) params.append('format', format);
      if (store_id) params.append('store_id', store_id);
      if (employee_id) params.append('employee_id', employee_id);
      
      const response = await axiosInstance.get(`/api/incentive-transactions/export/?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `incentive_data_${year || 'all'}_${month || 'all'}.${format}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return { success: true, message: 'Data exported successfully' };
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to export incentive data');
    }
  }
);

// =============================================================================
// STORE-SPECIFIC OPERATIONS
// =============================================================================

/**
 * Get store incentive statistics
 * GET /api/stores/:id/incentive_stats/
 */
export const fetchStoreIncentiveStatsThunk = createAsyncThunk(
  'employees/fetchStoreIncentiveStats',
  async ({ storeId, year, month }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      
      const response = await axiosInstance.get(`/api/stores/${storeId}/incentive_stats/?${params}`);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Failed to fetch store incentive stats');
    }
  }
);

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Helper function to format API errors
 */
export const formatAPIError = (error) => {
  if (error.response?.data) {
    if (typeof error.response.data === 'string') {
      return error.response.data;
    }
    if (error.response.data.detail) {
      return error.response.data.detail;
    }
    if (error.response.data.message) {
      return error.response.data.message;
    }
  }
  return error.message || 'An unexpected error occurred';
};

/**
 * Helper function to build query parameters
 */
export const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value);
    }
  });
  return searchParams;
};